import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  MessageSquare,
  ShieldCheck,
  BookOpen,
  ArrowRight,
  Check,
  Star,
  Loader2,
  X as CrossIcon,
  Clock,
  Lock,
  Sparkles,
  Menu,
  Zap,
  Scale,
} from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { startCheckout, setPendingPlan, consumePendingPlan } from "../lib/flowCheckout";
import PaymentGatewayModal from "../components/PaymentGatewayModal";
import TrustBar from "../components/TrustBar";
import StatsBar from "../components/StatsBar";
import BotPortrait from "../components/BotPortrait";
import FAQ from "../components/FAQ";

const HERO_IMAGE = "/hero-prelegal-v3.jpg";
const HERO_IMAGE_MOBILE = "/hero-prelegal-v3-mobile.jpg";

const LandingPage = ({ chatPath = "/" }) => {
  const { user } = useAuth();
  const isAuthed = !!user && user !== false;
  const ctaTarget = isAuthed ? chatPath : "/register";
  const navigate = useNavigate();
  const [checkoutLoading, setCheckoutLoading] = useState("");
  const [checkoutError, setCheckoutError] = useState("");
  const [gatewayModal, setGatewayModal] = useState({ open: false, planCode: "" });
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleSubscribe = async (planCode) => {
    setCheckoutError("");
    if (!isAuthed) {
      // Persist intent so we can resume the checkout after registration/login.
      // We don't know which gateway yet — that's picked after auth.
      setPendingPlan(planCode);
      navigate("/register", { state: { pendingPlan: planCode } });
      return;
    }
    // Authenticated: open gateway selector
    setGatewayModal({ open: true, planCode });
  };

  const handleGatewaySelected = async (gateway) => {
    const planCode = gatewayModal.planCode;
    if (!planCode) return;
    setCheckoutLoading(gateway);
    const res = await startCheckout(planCode, gateway);
    if (!res.ok) {
      setCheckoutLoading("");
      setCheckoutError(res.error || "No se pudo iniciar el pago.");
    }
  };

  const closeGatewayModal = () => {
    if (checkoutLoading) return; // don't close mid-redirect
    setGatewayModal({ open: false, planCode: "" });
    setCheckoutError("");
  };

  // Safety net: if the user just authenticated and we still have a pending
  // plan stashed (e.g. they registered via a different path), open the
  // gateway selector automatically the first time they land here as authed.
  useEffect(() => {
    if (!isAuthed) return;
    const { plan, gateway } = consumePendingPlan();
    if (!plan) return;
    if (gateway) {
      // User had already chosen a gateway before being redirected to auth
      setCheckoutLoading(gateway);
      startCheckout(plan, gateway).then((res) => {
        if (!res.ok) {
          setCheckoutLoading("");
          setCheckoutError(res.error || "No se pudo iniciar el pago.");
        }
      });
    } else {
      setGatewayModal({ open: true, planCode: plan });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthed]);

  return (
    <div className="min-h-screen w-full" style={{ background: "transparent", color: "var(--pl-text)" }}>
      {/* NAVBAR */}
      <header
        data-testid="landing-nav"
        className="sticky top-0 z-40"
        style={{
          background: "rgba(10, 12, 16, 0.72)",
          backdropFilter: "blur(14px) saturate(160%)",
          WebkitBackdropFilter: "blur(14px) saturate(160%)",
          borderBottom: "1px solid var(--pl-border)",
        }}
      >
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-3 flex items-center justify-between">
          <Link
            to="/"
            onClick={(e) => {
              if (window.location.pathname === "/") {
                e.preventDefault();
                window.scrollTo({ top: 0, behavior: "smooth" });
              }
            }}
            className="flex items-center gap-2.5"
            data-testid="nav-logo"
          >
            <ColumnLogo size={32} />
            <span className="font-bold text-lg tracking-tight" style={{ color: "var(--pl-text-strong)" }}>
              Pre-Legal<span style={{ color: "var(--pl-primary)" }}>.cl</span>
            </span>
          </Link>
          <nav className="hidden md:flex items-center gap-7 text-sm" style={{ color: "var(--pl-text)" }}>
            <a href="#why" data-testid="nav-link-why" className="transition-colors hover:text-cyan-300">¿Por qué Pre-Legal?</a>
            <a href="#bot" data-testid="nav-link-bot" className="transition-colors hover:text-cyan-300">PL</a>
            <a href="#planes" data-testid="nav-link-planes" className="transition-colors hover:text-cyan-300">Planes</a>
            <a href="#faq" data-testid="nav-link-faq" className="transition-colors hover:text-cyan-300">Preguntas</a>
          </nav>
          <div className="flex items-center gap-2">
            {isAuthed ? (
              <Link
                to={chatPath}
                data-testid="nav-go-chat"
                className="px-4 py-2 rounded-lg text-sm font-semibold shadow-sm hover:shadow-md transition-all"
                style={{ background: "var(--pl-primary)", color: "#001018" }}
              >
                Ir al chat
              </Link>
            ) : (
              <>
                <Link
                  to="/login"
                  data-testid="nav-login"
                  className="hidden sm:inline-flex px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-semibold transition-colors"
                  style={{ color: "var(--pl-text)" }}
                >
                  Iniciar sesión
                </Link>
                <Link
                  to="/register"
                  data-testid="nav-signup"
                  className="px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-semibold shadow-sm hover:shadow-md transition-all"
                  style={{ background: "var(--pl-primary)", color: "#001018" }}
                >
                  Crear cuenta
                </Link>
              </>
            )}
            {/* Mobile hamburger — only visible on small screens */}
            <button
              type="button"
              className="md:hidden inline-flex items-center justify-center rounded-lg w-10 h-10 ml-1 transition-colors"
              onClick={() => setMobileMenuOpen(true)}
              aria-label="Abrir menú"
              data-testid="nav-mobile-toggle"
              style={{
                background: "rgba(0, 200, 255, 0.10)",
                border: "1px solid rgba(0, 200, 255, 0.30)",
                color: "var(--pl-primary)",
              }}
            >
              <Menu className="w-5 h-5" />
            </button>
          </div>
        </div>
        {/* Cyan glow accent bar */}
        <div
          aria-hidden="true"
          data-testid="cyan-accent-band"
          style={{
            height: 1,
            width: "100%",
            background:
              "linear-gradient(90deg, transparent 0%, rgba(0,200,255,0.6) 50%, transparent 100%)",
          }}
        />
      </header>

      {/* Mobile menu drawer */}
      {mobileMenuOpen && (
        <div
          data-testid="nav-mobile-drawer"
          className="md:hidden fixed inset-0 z-50 flex flex-col"
          style={{ background: "rgba(5, 7, 10, 0.96)", backdropFilter: "blur(16px)" }}
        >
          <div
            className="flex items-center justify-between px-5 py-3"
            style={{ borderBottom: "1px solid var(--pl-border)" }}
          >
            <div className="flex items-center gap-2.5">
              <ColumnLogo size={28} />
              <span className="font-bold text-base tracking-tight" style={{ color: "var(--pl-text-strong)" }}>
                Pre-Legal<span style={{ color: "var(--pl-primary)" }}>.cl</span>
              </span>
            </div>
            <button
              type="button"
              onClick={() => setMobileMenuOpen(false)}
              aria-label="Cerrar menú"
              data-testid="nav-mobile-close"
              className="w-10 h-10 inline-flex items-center justify-center rounded-lg"
              style={{
                background: "rgba(255, 255, 255, 0.05)",
                border: "1px solid var(--pl-border)",
                color: "var(--pl-text)",
              }}
            >
              <CrossIcon className="w-5 h-5" />
            </button>
          </div>
          <nav
            className="flex flex-col gap-1 px-4 py-6 text-base font-semibold"
            style={{ color: "var(--pl-text-strong)" }}
          >
            {[
              { href: "#why", label: "¿Por qué Pre-Legal?" },
              { href: "#bot", label: "PL" },
              { href: "#planes", label: "Planes" },
              { href: "#faq", label: "Preguntas" },
            ].map((item) => (
              <a
                key={item.href}
                href={item.href}
                onClick={() => setMobileMenuOpen(false)}
                data-testid={`nav-mobile-${item.href.replace("#", "")}`}
                className="px-4 py-3.5 rounded-xl transition-colors"
                style={{
                  background: "rgba(0, 200, 255, 0.05)",
                  border: "1px solid rgba(0, 200, 255, 0.18)",
                }}
              >
                {item.label}
              </a>
            ))}
            {!isAuthed && (
              <Link
                to="/login"
                onClick={() => setMobileMenuOpen(false)}
                data-testid="nav-mobile-login"
                className="mt-3 px-4 py-3.5 rounded-xl text-center"
                style={{
                  background: "transparent",
                  border: "1px solid var(--pl-border-strong)",
                  color: "var(--pl-text)",
                }}
              >
                Iniciar sesión
              </Link>
            )}
          </nav>
        </div>
      )}

      {/* HERO — split layout (image right + text overlay left) — matches new mockup */}
      <section
        className="relative"
        data-testid="hero-section"
        style={{
          width: "100vw",
          marginLeft: "calc(50% - 50vw)",
          marginRight: "calc(50% - 50vw)",
          background: "#0a0c10",
          overflow: "hidden",
        }}
      >
        <div
          className="hero-split-grid"
          style={{
            display: "grid",
            gridTemplateColumns: "minmax(0, 1.05fr) minmax(0, 1fr)",
            minHeight: "clamp(560px, 70vw, 820px)",
            maxWidth: 1440,
            margin: "0 auto",
            position: "relative",
          }}
        >
          {/* LEFT — Copy + CTAs over dark background */}
          <div
            className="hero-copy"
            style={{
              padding: "clamp(36px, 6vw, 84px) clamp(20px, 4vw, 64px)",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              position: "relative",
              zIndex: 2,
              background:
                "linear-gradient(90deg, #050608 0%, rgba(5, 6, 8, 0.92) 60%, rgba(5, 6, 8, 0.0) 100%)",
            }}
          >
            <h1
              className="hero-headline font-extrabold tracking-tight"
              style={{
                letterSpacing: "-0.025em",
                lineHeight: 1.08,
                fontSize: "clamp(30px, 3.8vw, 56px)",
                color: "var(--pl-text-strong)",
                margin: 0,
                whiteSpace: "nowrap",
              }}
            >
              Cuando nadie responde
              <br />
              y te sientes perdido,
              <br />
              <span
                style={{
                  color: "var(--pl-primary)",
                  textShadow: "0 0 30px rgba(0, 200, 255, 0.4)",
                }}
              >
                necesitas claridad.
              </span>
            </h1>
            <p
              className="mt-6"
              style={{
                color: "var(--pl-text-muted)",
                fontSize: "clamp(16px, 1.35vw, 19px)",
                lineHeight: 1.55,
                maxWidth: 540,
              }}
            >
              Entiende tu caso, tus opciones y tus próximos pasos{" "}
              <span style={{ color: "var(--pl-primary)", fontWeight: 600 }}>
                en segundos
              </span>{" "}
              — sin tecnicismos.
            </p>

            {/* CTA */}
            <div className="mt-8">
              <Link
                to="/register"
                data-testid="hero-primary-cta"
                className="group inline-flex items-center gap-3 rounded-xl font-semibold transition-all hover:-translate-y-0.5"
                style={{
                  background: "var(--pl-primary)",
                  color: "#001018",
                  padding: "18px 32px",
                  fontSize: 17,
                  boxShadow: "0 14px 40px rgba(0, 200, 255, 0.32)",
                }}
              >
                <MessageSquare className="w-5 h-5" />
                Consultar mi caso gratis
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>

            {/* 3 feature pills */}
            <div
              className="mt-8 flex flex-wrap items-center gap-x-7 gap-y-3"
              data-testid="hero-feature-pills"
            >
              <FeaturePill
                icon={<ShieldCheck className="w-4 h-4" />}
                text={
                  <>
                    3 preguntas
                    <br />
                    gratis
                  </>
                }
              />
              <FeaturePill
                icon={<Zap className="w-4 h-4" />}
                text={
                  <>
                    Respuesta en
                    <br />
                    ~20 segundos
                  </>
                }
              />
              <FeaturePill
                icon={<ShieldCheck className="w-4 h-4" />}
                text={
                  <>
                    Hecho para
                    <br />
                    Chile
                  </>
                }
              />
            </div>

            {/* Social proof */}
            <div
              className="mt-9 flex items-center gap-3"
              data-testid="hero-social-proof"
            >
              <AvatarStack count={988} />
              <p
                style={{
                  color: "#ffffff",
                  fontSize: 15,
                  fontWeight: 700,
                  lineHeight: 1.45,
                  maxWidth: 360,
                  margin: 0,
                }}
              >
                Más de 1.000 personas ya han preparado su caso con{" "}
                <span style={{ color: "var(--pl-primary)", fontWeight: 800 }}>
                  Pre-Legal
                </span>
              </p>
            </div>
          </div>

          {/* RIGHT — Hero image */}
          <div
            className="hero-image-wrap"
            data-testid="hero-image-card"
            style={{ position: "relative", overflow: "hidden" }}
          >
            <picture>
              <source media="(max-width: 768px)" srcSet={HERO_IMAGE_MOBILE} />
              <img
                src={HERO_IMAGE}
                alt="Pre-Legal.cl — abogado revisando documentos"
                loading="eager"
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  objectPosition: "center right",
                  display: "block",
                }}
              />
            </picture>
            {/* Soft fade from left to blend with copy column */}
            <div
              aria-hidden="true"
              className="hero-image-fade"
              style={{
                position: "absolute",
                inset: 0,
                background:
                  "linear-gradient(90deg, rgba(5, 6, 8, 0.55) 0%, rgba(5, 6, 8, 0.0) 35%)",
                pointerEvents: "none",
              }}
            />
          </div>
        </div>

        {/* INNER TRUST BAND (3 columns at the bottom of the hero, dark) */}
        <div
          className="hero-trust-band"
          data-testid="hero-trust-band"
          style={{
            background: "rgba(8, 11, 16, 0.92)",
            borderTop: "1px solid rgba(0, 200, 255, 0.14)",
            borderBottom: "1px solid rgba(0, 200, 255, 0.14)",
            backdropFilter: "blur(8px)",
            WebkitBackdropFilter: "blur(8px)",
          }}
        >
          <div
            className="grid grid-cols-1 sm:grid-cols-3 gap-y-5 gap-x-6"
            style={{
              maxWidth: 1280,
              margin: "0 auto",
              padding: "22px clamp(20px, 4vw, 64px)",
            }}
          >
            <HeroTrustItem
              icon={<Lock className="w-5 h-5" />}
              title="100% confidencial"
              body="Tus datos están protegidos"
            />
            <HeroTrustItem
              icon={<Scale className="w-5 h-5" />}
              title="Basado en normativa chilena"
              body="Información actualizada y confiable"
            />
            <HeroTrustItem
              icon={<Check className="w-5 h-5" />}
              title="Sin tecnicismos"
              body="Hablamos en simple"
            />
          </div>
        </div>
      </section>

      {/* TRUST BAND */}
      <section className="px-5 sm:px-8 -mt-4">
        <div
          className="max-w-5xl mx-auto rounded-2xl px-6 py-5 grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6"
          style={{
            background: "var(--pl-card)",
            boxShadow: "var(--pl-shadow-md)",
            border: "1px solid var(--pl-border)",
          }}
          data-testid="trust-band"
        >
          <TrustItem icon={<Lock className="w-5 h-5" />} title="Confidencial" body="Tus consultas son privadas." />
          <TrustItem icon={<Clock className="w-5 h-5" />} title="Atención 24/7" body="Sin esperas, sin agendar." />
          <TrustItem icon={<BookOpen className="w-5 h-5" />} title="Jurisprudencia real" body="Cita fallos del Poder Judicial." />
        </div>
      </section>

      {/* STATS BAR */}
      <StatsBar />

      {/* WHY PRELEGAL */}
      <section id="why" className="py-24 px-5 sm:px-8" data-testid="why-section">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <h2
              className="text-3xl sm:text-4xl font-bold tracking-tight mb-3"
              style={{ color: "var(--pl-text-strong)", letterSpacing: "-0.02em" }}
            >
              ¿Por qué Pre-Legal.cl?
            </h2>
            <p className="text-base font-semibold" style={{ color: "var(--pl-primary)" }}>
              No es un asistente de IA genérico — es un abogado virtual especializado en Chile
            </p>
            <p className="mt-3 text-sm sm:text-base max-w-2xl mx-auto" style={{ color: "var(--pl-text-muted)" }}>
              Los asistentes de IA generales pueden ayudarte con muchas cosas, pero cuando se trata
              de derecho chileno, la diferencia es enorme.
            </p>
          </div>

          <ComparisonTable
            groups={[
              {
                title: "Fuentes legales",
                rows: [
                  { pre: "Conectado al Poder Judicial de Chile — cita fallos reales con número de Rol y fecha exacta", gen: "Sin acceso al Poder Judicial — puede inventar fallos que no existen" },
                  { pre: "Base de datos con el Código Civil, Penal, Tributario, de Procedimiento Civil y Penal de Chile", gen: "Conocimiento general de múltiples países, sin especialización en la legislación chilena" },
                  { pre: "Conectado a la Biblioteca del Congreso Nacional (BCN) con leyes y normativas vigentes", gen: "Sin acceso a bases de datos jurídicas oficiales de Chile" },
                  { pre: "Información legal actualizada y verificada en tiempo real", gen: "Conocimiento con fecha de corte — puede estar desactualizado" },
                ],
              },
              {
                title: "Precisión y confiabilidad",
                rows: [
                  { pre: "Si no encuentra la norma o el fallo, lo dice — nunca inventa respuestas", gen: "Puede dar respuestas incorrectas con total seguridad y sin advertirte" },
                  { pre: "Cada respuesta está respaldada por jurisprudencia y normativa real verificable", gen: "Respuestas basadas en patrones de texto, no en fuentes legales verificadas" },
                  { pre: "Especializado en áreas clave: laboral, arrendamiento, familia, consumidor, penal y tributario", gen: "Conocimiento superficial de todas las áreas, profundo en ninguna" },
                ],
              },
              {
                title: "Orientación práctica",
                rows: [
                  { pre: "Te dice exactamente qué papeles necesitas, qué tribunal corresponde y qué plazos tienes", gen: "Respuestas genéricas sin orientación práctica para Chile" },
                  { pre: "Redacta demandas, escritos y cartas legales adaptadas a la legislación chilena", gen: "Puede redactar textos legales genéricos que no cumplen requisitos chilenos" },
                  { pre: "Disponible 24/7 — sin esperar hora ni pagar consulta de $50.000 o más", gen: "Disponible 24/7 pero sin especialización legal real" },
                ],
              },
              {
                title: "Privacidad y seguridad",
                rows: [
                  { pre: "Tu consulta legal es privada — no alimenta modelos externos ni se comparte con terceros", gen: "Tus datos pueden ser usados para entrenar modelos de terceros" },
                  { pre: "Plataforma diseñada exclusivamente para consultas legales — sin distracciones", gen: "Plataforma general donde tu contexto legal se mezcla con miles de otros usos" },
                ],
              },
            ]}
          />
        </div>
      </section>

      {/* BOT PORTRAIT — PL en estilo Camila */}
      <BotPortrait chatPath={chatPath} />

      {/* PLANS */}
      <section id="planes" className="py-20 px-5 sm:px-8" data-testid="plans-section">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <h2
              className="text-3xl sm:text-4xl font-bold tracking-tight mb-3"
              style={{ color: "var(--pl-text-strong)", letterSpacing: "-0.02em" }}
            >
              Empieza gratis. Crece cuando lo necesites.
            </h2>
            <p className="text-base max-w-xl mx-auto" style={{ color: "var(--pl-text-muted)" }}>
              Sin contratos largos, sin letra chica. Cancelas cuando quieras directamente desde tu cuenta.
            </p>
          </div>

          <TrustBar />

          {checkoutError && (
            <div
              data-testid="plans-error"
              className="max-w-md mx-auto mb-6 rounded-xl px-4 py-3 text-sm"
              style={{
                background: "#fef2f2",
                border: "1px solid #fecaca",
                color: "#991b1b",
              }}
            >
              {checkoutError}
            </div>
          )}

          <div className="max-w-3xl mx-auto grid grid-cols-1 sm:grid-cols-2 gap-5">
            <PlanCard
              testId="plan-free"
              name="Gratis"
              tagline="Para probar el servicio"
              priceLabel="$0"
              priceSuffix=""
              features={[
                "3 preguntas de prueba",
                "Sin tarjeta requerida",
                "Acceso al corpus completo",
                "No se renuevan",
              ]}
              footnote="Las preguntas no se renuevan — son solo para probar."
              ctaLabel="Empezar gratis"
              ctaTestId="plan-free-btn"
              ctaHref={ctaTarget}
            />
            <PlanCard
              testId="plan-mensual"
              name="Plan Mensual"
              tagline="Para uso regular"
              priceLabel="$9.990"
              priceSuffix="/mes"
              features={[
                "Preguntas ilimitadas",
                "Documentos sin límite",
                "Citas verificadas",
                "Cancela cuando quieras",
              ]}
              ctaLabel="Suscribirse"
              ctaTestId="plan-mensual-btn"
              featured
              loading={!!checkoutLoading || gatewayModal.open}
              onClick={() => handleSubscribe("mensual")}
            />
          </div>
        </div>
      </section>

      {/* FAQ */}
      <FAQ />

      {/* CTA */}
      <section className="py-20 px-5 sm:px-8">
        <div
          className="max-w-3xl mx-auto rounded-3xl px-8 py-14 text-center relative overflow-hidden"
          style={{
            background: "linear-gradient(135deg, #00C8FF 0%, #0891B2 55%, #006B8F 100%)",
            boxShadow: "0 24px 70px rgba(0, 200, 255, 0.35), inset 0 1px 0 rgba(255,255,255,0.18)",
            border: "1px solid rgba(0, 200, 255, 0.45)",
            color: "#001018",
          }}
          data-testid="bottom-cta"
        >
          {/* decorative glow */}
          <div
            aria-hidden="true"
            style={{
              position: "absolute",
              top: "-40%",
              right: "-10%",
              width: "60%",
              height: "180%",
              background:
                "radial-gradient(closest-side, rgba(255,255,255,0.22) 0%, transparent 70%)",
              pointerEvents: "none",
            }}
          />
          <h2
            className="text-2xl sm:text-3xl font-bold tracking-tight relative"
            style={{ color: "#001018" }}
          >
            ¿Tienes una duda legal ahora?
          </h2>
          <p
            className="mt-3 text-sm sm:text-base max-w-lg mx-auto relative"
            style={{ color: "rgba(0, 16, 24, 0.82)" }}
          >
            Pruébalo gratis con 3 preguntas, sin tarjeta. Si te sirve, suscríbete por menos de lo
            que cuesta una sola consulta presencial.
          </p>
          <Link
            to={ctaTarget}
            data-testid="bottom-cta-btn"
            className="inline-flex items-center gap-2 mt-7 px-7 py-3.5 rounded-xl text-base font-semibold transition-all relative hover:-translate-y-0.5"
            style={{
              background: "#0a0c10",
              color: "var(--pl-primary)",
              border: "1.5px solid rgba(0, 200, 255, 0.55)",
              boxShadow: "0 10px 28px rgba(0, 16, 24, 0.45)",
            }}
          >
            {isAuthed ? "Ir al chat" : "Empezar gratis"}
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="px-5 sm:px-8 pb-12">
        <div
          className="max-w-6xl mx-auto rounded-2xl px-6 py-8 flex flex-col sm:flex-row items-center justify-between gap-4"
          style={{
            background: "var(--pl-card)",
            border: "1px solid var(--pl-border)",
          }}
        >
          <div className="flex items-center gap-2.5">
            <ColumnLogo size={26} />
            <span className="text-sm font-bold" style={{ color: "var(--pl-text-strong)" }}>
              Pre-Legal<span style={{ color: "var(--pl-primary)" }}>.cl</span>
            </span>
          </div>
          <div className="flex flex-wrap justify-center gap-x-5 gap-y-1.5 text-xs" style={{ color: "var(--pl-text-muted)" }}>
            <Link to="/prelegal/terminos" className="hover:text-blue-700 transition-colors">Términos</Link>
            <Link to="/prelegal/privacidad" className="hover:text-blue-700 transition-colors">Privacidad</Link>
            <Link to="/prelegal/aviso-legal" className="hover:text-blue-700 transition-colors">Aviso legal</Link>
            <a href="mailto:soporte@acbothub.com" className="hover:text-blue-700 transition-colors">soporte@acbothub.com</a>
          </div>
          <div className="text-xs" style={{ color: "var(--pl-text-subtle)" }}>
            © {new Date().getFullYear()} Pre-Legal.cl
          </div>
        </div>
      </footer>

      <PaymentGatewayModal
        open={gatewayModal.open}
        onClose={closeGatewayModal}
        onSelect={handleGatewaySelected}
        loading={checkoutLoading}
        error={checkoutError}
      />
    </div>
  );
};

// =================================================================
// Sub-components
// =================================================================
const ColumnLogo = ({ size = 32 }) => (
  <span
    className="inline-flex items-center justify-center shrink-0"
    style={{
      width: size,
      height: size,
    }}
  >
    <img
      src="/logo-prelegal.png"
      alt="Pre-Legal.cl"
      width={size}
      height={size}
      style={{
        width: size,
        height: size,
        objectFit: "contain",
        display: "block",
      }}
    />
  </span>
);

const TrustItem = ({ icon, title, body }) => (
  <div className="flex items-start gap-3">
    <div
      className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
      style={{ background: "var(--pl-primary-soft)", color: "var(--pl-primary)" }}
    >
      {icon}
    </div>
    <div>
      <div className="text-sm font-semibold" style={{ color: "var(--pl-text-strong)" }}>{title}</div>
      <div className="text-xs" style={{ color: "var(--pl-text-muted)" }}>{body}</div>
    </div>
  </div>
);

// Hero feature pill (icon + 2-line text) — used in the new hero
const FeaturePill = ({ icon, text }) => (
  <div className="flex items-center gap-2.5">
    <div
      className="w-9 h-9 rounded-full flex items-center justify-center shrink-0"
      style={{
        background: "rgba(0, 200, 255, 0.10)",
        border: "1px solid rgba(0, 200, 255, 0.32)",
        color: "var(--pl-primary)",
      }}
    >
      {icon}
    </div>
    <div
      style={{
        fontSize: 13.5,
        lineHeight: 1.25,
        fontWeight: 600,
        color: "var(--pl-text-strong)",
      }}
    >
      {text}
    </div>
  </div>
);

// Avatar stack used in the hero social proof section
const AvatarStack = ({ count = 988 }) => {
  const avatars = [
    { src: "/avatars/avatar1.jpg?v=2", alt: "Cliente 1" },
    { src: "/avatars/avatar2.jpg?v=2", alt: "Cliente 2" },
    { src: "/avatars/avatar3.jpg?v=2", alt: "Cliente 3" },
  ];
  return (
    <div className="flex items-center" style={{ flexShrink: 0 }}>
      {avatars.map((a, i) => (
        <img
          key={i}
          src={a.src}
          alt={a.alt}
          loading="lazy"
          style={{
            width: 52,
            height: 52,
            borderRadius: "50%",
            objectFit: "cover",
            border: "3px solid #0a0c10",
            marginLeft: i === 0 ? 0 : -16,
            boxShadow: "0 3px 10px rgba(0,0,0,0.45)",
            background: "#1a1d23",
          }}
        />
      ))}
      <div
        style={{
          width: 56,
          height: 56,
          borderRadius: "50%",
          background: "#1f2937",
          border: "3px solid #0a0c10",
          color: "#ffffff",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 14,
          fontWeight: 800,
          marginLeft: -16,
          letterSpacing: "0.01em",
          boxShadow: "0 3px 10px rgba(0,0,0,0.45)",
          flexShrink: 0,
        }}
      >
        +{count}
      </div>
    </div>
  );
};

// Hero inner trust band item (3 columns at the bottom of hero)
const HeroTrustItem = ({ icon, title, body }) => (
  <div className="flex items-center gap-3">
    <div
      className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0"
      style={{
        background: "rgba(0, 200, 255, 0.10)",
        border: "1px solid rgba(0, 200, 255, 0.28)",
        color: "var(--pl-primary)",
      }}
    >
      {icon}
    </div>
    <div>
      <div
        style={{
          fontSize: 14,
          fontWeight: 700,
          color: "var(--pl-text-strong)",
          lineHeight: 1.25,
        }}
      >
        {title}
      </div>
      <div
        style={{
          fontSize: 12.5,
          color: "var(--pl-text-muted)",
          lineHeight: 1.35,
          marginTop: 2,
        }}
      >
        {body}
      </div>
    </div>
  </div>
);

const PlanCard = ({
  name, tagline, priceLabel, priceSuffix, features, footnote,
  ctaLabel, ctaTestId, ctaHref, testId,
  featured = false, loading = false, onClick,
}) => {
  const button = (
    <button
      type="button"
      data-testid={ctaTestId}
      onClick={onClick}
      disabled={loading}
      className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl text-sm font-semibold transition-all"
      style={
        featured
          ? { background: "var(--pl-primary)", color: "#001018", boxShadow: "0 10px 28px rgba(0,200,255,0.30)" }
          : { background: "rgba(0,200,255,0.08)", color: "var(--pl-primary)", border: "1px solid var(--pl-border-accent)" }
      }
    >
      {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : ctaLabel}
    </button>
  );
  return (
    <div
      data-testid={testId}
      className="relative rounded-2xl p-7 flex flex-col"
      style={{
        background: featured
          ? "linear-gradient(165deg, rgba(0,200,255,0.08) 0%, rgba(0,200,255,0.02) 100%)"
          : "var(--pl-card)",
        border: featured ? "1.5px solid var(--pl-primary)" : "1px solid var(--pl-border)",
        boxShadow: featured ? "0 22px 60px rgba(0, 200, 255, 0.15)" : "var(--pl-shadow-sm)",
        transform: featured ? "translateY(-6px)" : "none",
      }}
    >
      {featured && (
        <span
          className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-[10px] font-bold tracking-widest uppercase"
          style={{ background: "var(--pl-primary)", color: "#001018" }}
        >
          <Star className="inline w-3 h-3 mr-1 -mt-0.5" /> Más popular
        </span>
      )}
      <div className="text-xs font-semibold tracking-widest uppercase mb-1.5" style={{ color: "var(--pl-text-muted)" }}>
        {name}
      </div>
      <div className="text-sm mb-5" style={{ color: "var(--pl-text-muted)" }}>{tagline}</div>
      <div className="flex items-baseline gap-1 mb-5">
        <span className="text-3xl font-extrabold tracking-tight" style={{ color: "var(--pl-text-strong)" }}>
          {priceLabel}
        </span>
        <span className="text-sm" style={{ color: "var(--pl-text-muted)" }}>{priceSuffix}</span>
      </div>
      <ul className="space-y-2.5 mb-6 flex-1">
        {features.map((f) => (
          <li key={f} className="flex items-start gap-2 text-sm" style={{ color: "var(--pl-text)" }}>
            <Check className="w-4 h-4 mt-0.5 shrink-0" style={{ color: "var(--pl-success)" }} />
            <span>{f}</span>
          </li>
        ))}
      </ul>
      {footnote && (
        <p className="text-xs italic mb-4 -mt-2" style={{ color: "var(--pl-text-subtle)" }}>
          {footnote}
        </p>
      )}
      {ctaHref && !onClick ? <Link to={ctaHref}>{button}</Link> : button}
    </div>
  );
};

// Comparison table
const ComparisonTable = ({ groups }) => (
  <div data-testid="comparison-table" className="space-y-7">
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-5xl mx-auto">
      <div
        className="rounded-xl px-5 py-4 flex items-center gap-3"
        style={{
          background: "linear-gradient(135deg, rgba(0,200,255,0.12) 0%, rgba(0,200,255,0.04) 100%)",
          border: "1.5px solid var(--pl-primary)",
          boxShadow: "0 8px 24px rgba(0, 200, 255, 0.12)",
        }}
      >
        <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
             style={{ background: "rgba(0, 200, 255, 0.18)" }}>
          <ColumnLogo size={22} />
        </div>
        <div>
          <div className="text-base font-semibold" style={{ color: "var(--pl-text-strong)" }}>Pre-Legal.cl</div>
          <div className="text-xs" style={{ color: "var(--pl-primary)" }}>Abogado virtual chileno</div>
        </div>
      </div>
      <div
        className="rounded-xl px-5 py-4 flex items-center gap-3"
        style={{
          background: "var(--pl-card-soft)",
          border: "1px solid var(--pl-border)",
        }}
      >
        <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
             style={{ background: "rgba(100, 116, 139, 0.10)", color: "var(--pl-text-muted)" }}>
          <Sparkles className="w-4 h-4" />
        </div>
        <div>
          <div className="text-base font-semibold" style={{ color: "var(--pl-text-strong)" }}>Asistente de IA general</div>
          <div className="text-xs" style={{ color: "var(--pl-text-muted)" }}>Sin especialización legal chilena</div>
        </div>
      </div>
    </div>
    {groups.map((g) => (
      <div key={g.title}>
        <h3 className="text-xs font-bold tracking-widest uppercase mb-3.5 px-1"
            style={{ color: "var(--pl-primary)", letterSpacing: "0.16em" }}>
          {g.title}
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-w-5xl mx-auto">
          <div className="space-y-2.5">
            {g.rows.map((r, i) => <ComparisonCell key={`p-${i}`} positive>{r.pre}</ComparisonCell>)}
          </div>
          <div className="space-y-2.5">
            {g.rows.map((r, i) => <ComparisonCell key={`g-${i}`} positive={false}>{r.gen}</ComparisonCell>)}
          </div>
        </div>
      </div>
    ))}
  </div>
);

const ComparisonCell = ({ children, positive }) => (
  <div
    className="rounded-xl px-4 py-3 flex items-start gap-3"
    style={
      positive
        ? {
            background: "linear-gradient(135deg, rgba(0,200,255,0.08) 0%, rgba(0,200,255,0.02) 100%)",
            border: "1px solid var(--pl-primary)",
            boxShadow: "0 2px 10px rgba(0, 200, 255, 0.10)",
          }
        : { background: "var(--pl-card-soft)", border: "1px solid var(--pl-border)" }
    }
  >
    <div
      className="w-5 h-5 rounded-full flex items-center justify-center shrink-0 mt-0.5"
      style={{
        background: positive ? "rgba(0,200,255,0.20)" : "rgba(100,116,139,0.12)",
      }}
    >
      {positive
        ? <Check className="w-3 h-3" style={{ color: "var(--pl-primary)" }} strokeWidth={3} />
        : <CrossIcon className="w-3 h-3" style={{ color: "#94a3b8" }} strokeWidth={3} />}
    </div>
    <p className="text-sm leading-relaxed" style={{ color: positive ? "var(--pl-text)" : "var(--pl-text-muted)" }}>
      {children}
    </p>
  </div>
);

export default LandingPage;
